/**
 * AppFolio-specific pure helpers extracted from the dialog/component layer so they
 * can be unit-tested and reused. Anything React-aware lives in the component file.
 */

import type { AppfolioConfig } from '@/Structures/types';
import type { UnitPmsConfig } from '@/Units/types';

/** A row parsed from the AppFolio Bulk Charges and Credits template. */
export interface AppfolioRow {
  propertyName: string;
  unitId: string;
  unitName: string;
  occupancyUid?: string;
  occupancyId?: string;
  tenantName?: string;
}

export interface ParseAppfolioResult {
  rows: AppfolioRow[];
  missingHeaders: string[];
}

/** Minimum headers we need to drive the matching UI. Other template columns
 * (Occupancy UID, Tenant Name, Occupancy ID, Amount, Description) are read when
 * present but not enforced, so customers who upload a stripped-down CSV still work. */
export const APPFOLIO_REQUIRED_HEADERS = ['Property Name', 'Unit ID', 'Unit Name'];

/**
 * Coerce the raw parsed CSV/XLSX rows (header-keyed dicts) into AppfolioRow[].
 * Returns `missingHeaders` when the file lacks required columns; the matching
 * dialog uses this to surface a clear toast and reset state.
 */
export function parseAppfolioRows(raw: Record<string, unknown>[]): ParseAppfolioResult {
  if (raw.length === 0) {
    return { rows: [], missingHeaders: APPFOLIO_REQUIRED_HEADERS };
  }
  const headerSet = new Set(Object.keys(raw[0] ?? {}));
  const missing = APPFOLIO_REQUIRED_HEADERS.filter((h) => !headerSet.has(h));
  if (missing.length > 0) {
    return { rows: [], missingHeaders: missing };
  }
  const rows: AppfolioRow[] = [];
  for (const r of raw) {
    const propertyName = String(r['Property Name'] ?? '').trim();
    const unitId = String(r['Unit ID'] ?? '').trim();
    const unitName = String(r['Unit Name'] ?? '').trim();
    if (!propertyName || !unitId) continue;
    const occupancyUid = String(r['Occupancy UID'] ?? '').trim();
    const occupancyId = String(r['Occupancy ID'] ?? '').trim();
    const tenantName = String(r['Tenant Name'] ?? '').trim();
    rows.push({
      propertyName,
      unitId,
      unitName,
      ...(occupancyUid && { occupancyUid }),
      ...(occupancyId && { occupancyId }),
      ...(tenantName && { tenantName }),
    });
  }
  return { rows, missingHeaders: [] };
}

/** Build the per-unit pms_config payload from a matched AppfolioRow. Captures the
 * occupancy fields when present so the export can post charges to the correct ledger. */
export function buildAppfolioUnitConfig(row: AppfolioRow): UnitPmsConfig {
  return {
    unit_id: row.unitId,
    unit_name: row.unitName,
    ...(row.occupancyUid && { occupancy_uid: row.occupancyUid }),
    ...(row.occupancyId && { occupancy_id: row.occupancyId }),
    ...(row.tenantName && { tenant_name: row.tenantName }),
  };
}

/** Build the structure-level pms_config from the matching dialog state. */
export function buildAppfolioStructureConfig(propertyName: string): AppfolioConfig {
  return { property_name: propertyName };
}

/** Extract the unique set of property names from a list of parsed rows. */
export function getDistinctAppfolioPropertyNames(rows: AppfolioRow[]): string[] {
  const names = new Set<string>();
  for (const r of rows) names.add(r.propertyName);
  return Array.from(names);
}
